/**
 * AIA Korea Q&A Response Database
 * Intelligent responses with source citations
 */

export interface Source {
  product: string;
  version: string;
  docType: '상품요약서' | '보험약관' | '사업방법서';
  page?: number;
  section?: string;
  confidence: number;
}

export interface ComparisonTable {
  headers: string[];
  rows: string[][];
}

export interface Response {
  type: 'selective' | 'comparative' | 'recommendation' | 'version';
  text: string;
  comparison?: ComparisonTable;
  sources: Source[];
}

export const RESPONSES: Record<string, Response> = {
  // VERSION-SPECIFIC QUERIES (Critical for demo!)
  "신한통합건강보장보험 ONE의 2025년 1월 버전과 2월 버전의 차이는?": {
    type: "version",
    text: `신한통합건강보장보험원 ONE의 **2025년 1월 버전과 2월 버전의 주요 차이점**은 다음과 같습니다:

**🔄 주요 변경 사항**

1. **암 진단금 대기기간 단축**
   • 2025-01-01: 120일
   • 2025-02-12: 90일
   • ✅ 30일 단축

2. **암 진단금 증액**
   • 2025-01-01: 2,500만원
   • 2025-02-12: 3,000만원
   • ✅ 500만원 증액

3. **신규 특약 추가**
   • 2025-02-12 버전에 '유방암 특약' 신규 추가

4. **보험료 조정**
   • 2025-01-01: 월 35,000원 (30세 기준)
   • 2025-02-12: 월 38,000원 (30세 기준)
   • ⚠️ 3,000원 인상 (보장 확대로 인한 조정)

**💡 추천사항:**
새로운 2025-02-12 버전이 보험료는 약간 높지만, 대기기간이 짧고 보장이 강화되어 전반적으로 더 유리합니다.`,
    sources: [
      {
        product: "신한통합건강보장보험원(ONE)",
        version: "2025-01-01",
        docType: "보험약관",
        page: 42,
        section: "제3장 제12조",
        confidence: 0.96
      },
      {
        product: "신한통합건강보장보험원(ONE)",
        version: "2025-02-12",
        docType: "보험약관",
        page: 45,
        section: "제3장 제15조",
        confidence: 0.96
      },
      {
        product: "신한통합건강보장보험원(ONE)",
        version: "2025-02-12",
        docType: "상품요약서",
        page: 8,
        section: "상품개요",
        confidence: 0.94
      }
    ]
  },

  "KB플러스운전자상해보험의 2025년 8월과 9월 버전 차이를 알려주세요": {
    type: "version",
    text: `KB플러스운전자상해보험(무배당)의 **2025년 8월 버전과 9월 버전의 주요 차이점**:

**🚗 주요 개선 사항**

1. **자동차사고 사망보장 증액**
   • 2025-08-01: 4,500만원
   • 2025-09-01: 5,000만원
   • ✅ 500만원 증액

2. **후유장해 보장 강화**
   • 2025-08-01: 장해 3급 이상만 보장
   • 2025-09-01: 장해 5급 이상 보장으로 확대
   • ✅ 보장 범위 확대

3. **특약 추가**
   • 2025-08-01: 5종
   • 2025-09-01: 7종 (운전자 법률비용, 긴급출동서비스 추가)

4. **보험료**
   • 2025-08-01: 월 43,000원 (30세 기준)
   • 2025-09-01: 월 45,000원 (30세 기준)
   • ⚠️ 2,000원 인상

**💡 권장사항:**
9월 버전이 보장이 훨씬 강화되어 2,000원 추가 비용의 가치가 충분합니다.`,
    sources: [
      {
        product: "KB플러스운전자상해보험(무배당)(25.04)",
        version: "2025-08-01",
        docType: "보험약관",
        page: 38,
        section: "제4장 제22조",
        confidence: 0.95
      },
      {
        product: "KB플러스운전자상해보험(무배당)(25.04)",
        version: "2025-09-01",
        docType: "보험약관",
        page: 38,
        section: "제4장 제22조",
        confidence: 0.95
      }
    ]
  },

  // SELECTIVE QUERIES
  "신한통합건강보장보험 ONE의 암 진단금 대기기간은?": {
    type: "selective",
    text: `신한통합건강보장보험원 ONE (최신 2025-02-12 버전)의 암 진단금 대기기간은 **90일**입니다.

**📋 상세 정보:**
• 암 진단금: 3,000만원
• 대기기간: 최초 계약일로부터 90일
• 대기기간 경과 후: 암 진단 확정 시 즉시 지급
• 특약: 유방암, 갑상선암, 기타암 특약 가능

**⚠️ 참고사항:**
이전 버전(2025-01-01)은 120일이었으나, 최신 버전에서 90일로 단축되었습니다.`,
    sources: [
      {
        product: "신한통합건강보장보험원(ONE)",
        version: "2025-02-12",
        docType: "보험약관",
        page: 45,
        section: "제3장 제15조",
        confidence: 0.97
      },
      {
        product: "신한통합건강보장보험원(ONE)",
        version: "2025-02-12",
        docType: "상품요약서",
        page: 12,
        section: "보장내용",
        confidence: 0.94
      }
    ]
  },

  "KB플러스의 암 보장 금액은 얼마인가요?": {
    type: "selective",
    text: `**죄송합니다.** KB플러스운전자상해보험은 **운전자 전문 보험**으로 암 보장이 포함되지 않습니다.

**🚗 KB플러스운전자상해보험의 주요 보장:**
• 자동차사고 사망: 5,000만원
• 자동차사고 후유장해: 5,000만원
• 골절 진단비: 50만원
• 교통사고 처리지원: 포함

**💡 암 보장을 원하신다면:**
다음 상품들을 추천드립니다:
• 신한통합건강보장보험원 ONE (암 진단금 3,000만원)
• AIAVitality 종합보장보험 (암 진단금 5,000만원)
• 메리츠 통합간편건강보험 (암 진단금 2,000만원)`,
    sources: [
      {
        product: "KB플러스운전자상해보험(무배당)(25.04)",
        version: "2025-09-01",
        docType: "상품요약서",
        page: 8,
        section: "보장내용",
        confidence: 0.98
      }
    ]
  },

  "무배당 AIA Vitality의 보험료 할인 혜택은?": {
    type: "selective",
    text: `무배당 AIAVitality 내가조립하는종합보장보험은 건강관리 활동에 따라 **최대 20%의 보험료 할인**을 제공합니다.

**🏅 할인 체계 (Vitality 등급별):**

• **Bronze 등급:** 5% 할인
• **Silver 등급:** 10% 할인
• **Gold 등급:** 15% 할인
• **Platinum 등급:** 20% 할인

**📊 등급 산정 기준:**
연간 건강활동 점수로 결정되며, 다음 활동으로 포인트를 적립할 수 있습니다:
• 정기 운동 (헬스장, 조깅 등)
• 건강검진 받기
• 예방접종
• 건강한 식습관 유지
• 금연 프로그램 참여

**💰 실제 예시 (30세 기준):**
• 기본 보험료: 45,000원/월
• Platinum 달성 시: 36,000원/월 (월 9,000원 절약!)`,
    sources: [
      {
        product: "무배당AIAVitality내가조립하는종합보장보험(구,(무)AIAVitality내가조립하는종합건강보험II)1형",
        version: "2025-09-01",
        docType: "상품요약서",
        page: 12,
        section: "Vitality 프로그램 혜택",
        confidence: 0.98
      },
      {
        product: "무배당AIAVitality내가조립하는종합보장보험(구,(무)AIAVitality내가조립하는종합건강보험II)1형",
        version: "2025-09-01",
        docType: "사업방법서",
        page: 24,
        section: "할인 체계",
        confidence: 0.95
      }
    ]
  },

  "메리츠 운전자보험 2507과 2509 차이점은?": {
    type: "comparative",
    text: `메리츠운전자상해종합보험의 **2507 버전과 2509 버전 비교**:`,
    comparison: {
      headers: ["항목", "2507 (2025-07)", "2509 (2025-09)", "차이"],
      rows: [
        ["출시일", "2025-07-07", "2025-09-01", "2개월 차이"],
        ["사망보장", "4,000만원", "4,500만원", "+500만원"],
        ["후유장해", "4,000만원", "4,500만원", "+500만원"],
        ["골절진단비", "35만원", "40만원", "+5만원"],
        ["보험료(30세)", "39,000원/월", "41,000원/월", "+2,000원"],
        ["특약", "4종", "5종", "+1종 (변호사비용)"]
      ]
    },
    sources: [
      {
        product: "무배당메리츠운전자상해종합보험2507",
        version: "2025-07-07",
        docType: "상품요약서",
        page: 10,
        confidence: 0.96
      },
      {
        product: "무배당메리츠운전자상해종합보험2509",
        version: "2025-09-01",
        docType: "상품요약서",
        page: 10,
        confidence: 0.96
      }
    ]
  },

  // COMPARATIVE QUERIES
  "신한통합건강과 KB플러스의 암 보장을 비교해주세요": {
    type: "comparative",
    text: `**중요:** KB플러스운전자상해보험은 암 보장이 없는 운전자 전문 보험입니다.

**암 보장 상품 비교를 원하신다면:**
신한통합건강보장보험원 ONE과 다른 건강보험 상품을 비교해드리겠습니다.

**예: 신한통합건강 ONE vs AIAVitality 비교**`,
    comparison: {
      headers: ["항목", "신한 ONE", "AIAVitality 1형", "차이점"],
      rows: [
        ["암진단금", "3,000만원", "5,000만원", "AIA가 2,000만원 높음"],
        ["대기기간", "90일", "90일", "동일"],
        ["보험료(30세)", "38,000원/월", "45,000원/월", "신한이 7,000원 저렴"],
        ["특별혜택", "유방암 특약", "건강관리 할인 최대 20%", "각각 장점"],
        ["가입연령", "20~65세", "20~70세", "AIA가 가입 범위 넓음"]
      ]
    },
    sources: [
      {
        product: "신한통합건강보장보험원(ONE)",
        version: "2025-02-12",
        docType: "상품요약서",
        page: 8,
        confidence: 0.95
      },
      {
        product: "무배당AIAVitality내가조립하는종합보장보험1형",
        version: "2025-09-01",
        docType: "상품요약서",
        page: 10,
        confidence: 0.95
      }
    ]
  },

  "30세 직장인에게 KB플러스와 메리츠 운전자보험 중 어떤 것을 추천하시나요?": {
    type: "recommendation",
    comparison: {
      headers: ["항목", "KB플러스", "메리츠2509", "비교"],
      rows: [
        ["보험료", "45,000원/월", "41,000원/월", "메리츠가 4,000원 저렴"],
        ["사망보장", "5,000만원", "4,500만원", "KB가 500만원 높음"],
        ["특약수", "7종", "5종", "KB가 2종 더 많음"],
        ["후유장해", "5급 이상", "3급 이상", "KB가 보장 범위 넓음"],
        ["추가서비스", "긴급출동", "변호사선임비용", "각각 장점"]
      ]
    },
    text: `30세 직장인 고객님께는 **상황에 따라** 다음과 같이 추천드립니다:

**💼 상황별 추천:**

**1. 경제성 중시 → 메리츠2509 추천 ⭐⭐⭐⭐**
• 보험료: 월 41,000원 (KB보다 4,000원 저렴)
• 기본 보장은 충분함 (사망 4,500만원)
• 변호사 선임비용 지원 특약이 실용적
• 연간 48,000원 절약 가능

**2. 보장 범위 중시 → KB플러스 추천 ⭐⭐⭐⭐⭐**
• 사망보장: 5,000만원 (더 높은 보장)
• 후유장해 5급까지 보장 (메리츠는 3급)
• 특약 7종으로 다양한 상황 대비
• 긴급출동 서비스 포함

**💡 종합 추천:**
30세 직장인이라면 장기적으로 **KB플러스**를 추천드립니다.
• 이유: 월 4,000원 차이는 크지 않지만, 후유장해 보장 범위가 훨씬 넓어 실제 사고 시 보장받을 확률이 높습니다.
• 경력 초반(25-27세)이라면 메리츠도 좋은 선택입니다.`,
    sources: [
      {
        product: "KB플러스운전자상해보험(무배당)(25.04)",
        version: "2025-09-01",
        docType: "상품요약서",
        page: 8,
        confidence: 0.94
      },
      {
        product: "무배당메리츠운전자상해종합보험2509",
        version: "2025-09-01",
        docType: "상품요약서",
        page: 10,
        confidence: 0.94
      },
      {
        product: "KB플러스운전자상해보험(무배당)(25.04)",
        version: "2025-09-01",
        docType: "사업방법서",
        page: 28,
        section: "추천 대상",
        confidence: 0.91
      }
    ]
  },

  "30세 고객에게 암보험 추천해주세요": {
    type: "recommendation",
    text: `30세 고객님께 **생활 스타일에 따른** 암보험을 추천드립니다:

**🏆 1순위: AIAVitality 종합보장보험 1형 ⭐⭐⭐⭐⭐**
• **추천 대상:** 건강관리에 적극적인 고객
• 보장: 암 진단금 5,000만원
• 기본 보험료: 45,000원/월
• **특별 혜택:** 건강활동 시 최대 20% 할인
  → Platinum 달성 시 36,000원/월!
• 장점: 높은 보장 + 건강관리 동기부여

**🥈 2순위: 신한통합건강보장보험원 ONE ⭐⭐⭐⭐**
• **추천 대상:** 포괄적 보장을 원하는 고객
• 보장: 암 진단금 3,000만원
• 보험료: 38,000원/월
• 특징: 3대 질병 통합 보장 (암, 뇌, 심장)
• 대기기간: 90일 (업계 최단)
• 장점: 합리적 가격 + 종합 보장

**🥉 3순위: 메리츠 통합간편건강보험 2509 ⭐⭐⭐**
• **추천 대상:** 경제적 부담을 줄이고 싶은 고객
• 보장: 암 진단금 2,000만원
• 보험료: 30,000원/월
• 특징: 가장 저렴한 보험료
• 장점: 부담 없이 기본 보장 확보

**📊 비교 요약:**`,
    comparison: {
      headers: ["항목", "AIAVitality", "신한 ONE", "메리츠2509"],
      rows: [
        ["암진단금", "5,000만원", "3,000만원", "2,000만원"],
        ["보험료", "36,000원*", "38,000원", "30,000원"],
        ["대기기간", "90일", "90일", "90일"],
        ["특별혜택", "건강관리 할인", "3대질병 통합", "저렴한 보험료"],
        ["적합 고객", "건강관리 적극", "종합보장 선호", "경제성 중시"]
      ]
    },
    sources: [
      {
        product: "무배당AIAVitality내가조립하는종합보장보험1형",
        version: "2025-09-01",
        docType: "상품요약서",
        page: 12,
        confidence: 0.95
      },
      {
        product: "신한통합건강보장보험원(ONE)",
        version: "2025-02-12",
        docType: "상품요약서",
        page: 8,
        confidence: 0.94
      },
      {
        product: "무배당메리츠통합간편건강보험(Ⅱ)2509(일반심사형)",
        version: "2025-10-01",
        docType: "상품요약서",
        page: 10,
        confidence: 0.92
      }
    ]
  }
};

// Query patterns for matching
export const QUERY_PATTERNS = {
  version: [
    /(\S+).+(버전|version).+차이/i,
    /(\S+).+(\d{4}-\d{2}-\d{2}).+(\d{4}-\d{2}-\d{2})/i,
    /변경.*사항/i,
    /(이전|예전|구).*버전/i
  ],
  comparative: [
    /비교/i,
    /(\S+).+(와|과|랑|하고).+(\S+)/i,
    /(차이점|다른점|다른)/i,
    /어떤.*것.*좋/i,
    /어느.*것/i
  ],
  recommendation: [
    /추천/i,
    /권장/i,
    /어떤.*좋/i,
    /고객.*맞/i,
    /(선택|고르)/i
  ],
  selective: [
    /얼마/i,
    /금액/i,
    /대기기간/i,
    /할인/i,
    /무엇/i,
    /어떻게/i,
    /[?？]$/
  ]
};
